<script lang="ts">
  import Arrow from "$components/arrow/index.svelte";
  import LinkComp from "$components/link.svelte";
  import DateComp from "$components/date-on-card/index.svelte";
  import { utils } from "$lib/utils";
  import ImageComp from "$components/image.svelte";
  import type { Performer } from "types/backend/models/performer.entity";
  import type { IPopularTicket } from "types/backend/models/widgets/popular-ticket.entity";
  import { serializeSchema, ticketToSchema } from "$lib/utils/ldjson";

  export let data: IPopularTicket[] = [];
  export let locale: string = "";
  function getPerformerColor(performers: Partial<Performer>[]) {
    const perf1 = (performers[0]?.attributes as any)?.colour || "#d2d2d2";
    const perf2 = (performers[1]?.attributes as any)?.colour || "#d2d2d2";
    return `background:linear-gradient(to bottom, ${perf1}, ${perf2});`;
  }

  function toSchema(ticket: IPopularTicket) {
    return serializeSchema(ticketToSchema(ticket));
  }

  let mobileVisible = 4;
  function loadMore(ev: Event) {
    ev.stopImmediatePropagation();
    mobileVisible += 5;
  }
</script>

<svelte:head>
  {#if data && data?.length}
    {#each data as item}
      {@html toSchema(item)}
    {/each}
  {/if}
</svelte:head>

<div class="popular-events-container">
  <div class="ticket-container">
    {#each data as item, i}
      {@const { title, datetime, slug, subcategory, venue, priceFrom, performers, tbc } = item}
      {@const background = getPerformerColor(performers)}
      {@const { title: venueTitle, address } = venue}
      <LinkComp href={slug} className={i >= mobileVisible ? "desk-only" : ""}>
        <article class="single-tickets grid" style={background}>
          <section class="inner grid">
            <DateComp date={datetime} {tbc} className={"mob-only"} />
            <div class="content grid">
              <div class="logos">
                {#if performers.length}
                  {#each performers as performer}
                    <ImageComp image={performer.attributes?.logo} className="team-logo" />
                  {/each}
                {/if}
              </div>
              <div class="text-container">
                <h3 class="pf-500">{title}</h3>
                <hr class="desk-only sep" />
                <div class="middle grid">
                  <time class="datetime desk-only pf-500">
                    {#if tbc?.date}
                      TBC
                    {:else}
                      {@html utils.getDateAndTime(datetime, locale, 3)}
                    {/if}
                  </time>
                  <p class="pf-300 subcategory">{subcategory?.title || ""}</p>
                  <p class="pf-500 cut-text" title={address?.city}>
                    {[venueTitle, address?.city].filter(v => v).join(", ")}
                  </p>
                </div>
                <hr class="desk-only sep" />
              </div>
            </div>

            <div class="arrow-wrapper grid mob-only">
              <div>
                <Arrow />
              </div>
            </div>
            <button class="desk-only sf-500">Book Now</button>
          </section>
        </article>
      </LinkComp>
    {/each}
  </div>
  {#if mobileVisible < data.length}
    <button class="mob-only btn load-more" on:click={loadMore}>Load More</button>
  {/if}
</div>

<style lang="scss">
  @use "@styles/breakpoint-mixins" as *;

  .popular-events-container {
    border-radius: 0.3125em;
    p {
      margin: 0;
    }
  }
  .ticket-container {
    display: grid;
    @include desktop {
      grid-template-columns: repeat(4, 23.85%);
      column-gap: 1.25em;
      row-gap: 3em;
    }
  }
  .single-tickets {
    margin: 0.5em 0;
    background: #fbfbfb;
    border: 1px solid #d8d8d8;
    border-radius: 0.5em;
    @include desktop {
      border: none;
      margin: 0;
      position: relative;
      padding: 1px;
      border-radius: 8px;
    }
  }

  .inner {
    border-radius: 0.4em;
    padding: 0.625em;
    grid-template-columns: 3.125em auto 1em;
    background: #fbfbfb;
    gap: 0;
    @include desktop {
      grid-template-columns: 1fr;
      min-height: 23em;
      padding: 1.625em 1.125em;
      grid-template-rows: auto 3.125em;
    }
  }

  .logos {
    display: flex;
    gap: 0.5em;
    width: 3.65em;
    height: 1.7em;
    :global(picture) {
      width: 50%;
    }

    @include desktop {
      width: 100%;
      padding: 0 1.875em;
      height: 3.125em;
      justify-content: space-between;
      :global(picture) {
        width: unset;
      }
    }
  }

  .content {
    grid-template-columns: 1fr;
    padding: 0 0.88em;
    grid-template-rows: 1.9em 1fr;
    @include desktop {
      grid-template-rows: 60px 1fr;
      padding: 0;
      text-align: center;
    }

    .text-container {
      display: grid;
      @include desktop {
        grid-template-rows: 1fr 1em 1fr 1em 1fr;
      }
      // Title
      h3 {
        margin: 0;
        font-size: 1em;
        line-height: 1.0625em;
        @include desktop {
          align-self: center;
          font-size: 1.25em;
          font-weight: 700;
        }
      }

      .middle {
        grid-template-columns: 1fr;
        // Subcategory and Venue
        @include desktop {
          gap: 0.4em;
        }
        p {
          line-height: 1em;
          font-size: 0.813em;
          @include desktop {
            font-size: 1em;
          }
        }
      }

      .sep {
        margin: auto;
        width: 2.1875em;
        height: 0.0625em;
      }

      .subcategory {
        @include desktop {
          font-weight: 500;
        }
      }

      .cut-text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .price {
        align-self: center;
        padding-bottom: 2em;
      }
    }
  }
  /* .info {
            display: none;
            @include desktop {
              display: grid;
              p {
                color: #000;
              }
              &.price {
                font-size: 1.25em;
              }
            }
          } */

  .arrow-wrapper {
    div {
      align-self: center;
      justify-self: end;
    }
  }

  button {
    height: 2.7em;
    padding: 0.4375em 1.5em;
    justify-content: center;
    align-items: center;
    gap: 0.625em;
  }

  .load-more {
    width: 100%;
    color: #fff;
    font-family: Nunito;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 30px; /* 187.5% */
  }
</style>
